<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";

import Editor from "@tinymce/tinymce-vue";

const router = useRouter();
const email = ref(null);
const password = ref(null);

const toastId = ref("");

// Forgot password state variables
const showForgotPasswordModal = ref(false);
const forgotPasswordEmail = ref("");
const isSubmitting = ref(false);

// Redirect if already authenticated
onMounted(() => {
	const token = localStorage.getItem("auth_token");
	const expiresAt = localStorage.getItem("expiresAt");

	if (token && expiresAt && new Date(expiresAt) > new Date()) {
		router.push("/");
	}
});

// Handle forgot password submission
const handleForgotPassword = async () => {
	// Email validation
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

	if (!forgotPasswordEmail.value) {
		toast.error("Email address is required", {
			type: "error",
		});
		return;
	}

	if (!emailRegex.test(forgotPasswordEmail.value)) {
		toast.error("Please enter a valid email address", {
			type: "error",
		});
		return;
	}

	isSubmitting.value = true;
	const forgotToastId = toast.loading("Processing your request...", {
		autoClose: false,
		type: "loading",
		isLoading: true,
	});

	try {
		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/auth/forgot-password`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ email: forgotPasswordEmail.value }),
			}
		);

		// Always show the same message regardless of the response to prevent email enumeration
		toast.update(forgotToastId, {
			render: "If this email exists in our system, you will receive a password reset link shortly.",
			autoClose: 5000,
			type: "success",
			isLoading: false,
		});

		// Close modal and reset form
		showForgotPasswordModal.value = false;
		forgotPasswordEmail.value = "";
	} catch (error) {
		console.error("Forgot password error:", error);
		toast.update(forgotToastId, {
			render: "There was an error processing your request. Please try again later.",
			autoClose: 5000,
			type: "error",
			isLoading: false,
		});
	} finally {
		isSubmitting.value = false;
	}
};

const handleSubmit = async () => {
	try {
		// toast.clearAll(null, true);

		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

		let error = false;

		if (!email.value.value) {
			toast.error("Email address is required", {
				type: "error",
			});

			error = true;
		} else if (!emailRegex.test(email.value.value)) {
			toast.error("The email address is invalid!", {
				type: "error",
			});
			error = true;
		}

		if (!password.value.value) {
			toast.error("A valid password is required!", {
				type: "error",
			});

			error = true;
		}

		if (error) {
			return false;
		}

		// Disable the email and password fields.

		// Collect email and password from form
		const userData = {
			email: email.value.value,
			password: password.value.value,
		};

		console.log("User data to send", userData);

		toastId.value = toast.loading(
			"Please wait while we validate your credentials",
			{
				autoClose: false,
				type: "loading",
				isLoading: true,
			}
		);

		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/auth/login`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(userData),
			}
		);

		if (!response.ok) {
			let msg = `Either the email or password or both are invalid! Please check and try again. If you are still unable to continue, please contact site administrator!`;

			try {
				let d = await response.json();
				if ("is_active" in d) {
					msg = d["message"];
				}
			} catch (err1) {}

			toast.update(toastId.value, {
				render: msg,
				autoClose: 5000,
				type: "error",
				isLoading: false,
			});

			return false;
		}

		// Parse the response data
		const data = await response.json();

		// Extract values from response
		const {
			token: {
				token,
				refresh_token,
				refresh_token_expires,
				expires_at: expiresAt,
			},
			user,
			tenant,
		} = data;

		// Store auth data in localStorage
		localStorage.setItem("auth_token", token);
		localStorage.setItem("user", JSON.stringify(user));
		localStorage.setItem("expiresAt", expiresAt);

		if (refresh_token) {
			localStorage.setItem("refresh_token", refresh_token);

			if (!refresh_token_expires) {
				// Calculate expiration date (1 year from now)
				const refreshExpiresAt = new Date();
				refreshExpiresAt.setFullYear(
					refreshExpiresAt.getFullYear() + 1
				);

				localStorage.setItem(
					"refresh_token_expires",
					refreshExpiresAt.toISOString()
				);
			} else {
				localStorage.setItem(
					"refresh_token_expires",
					refresh_token_expires
				);
			}
		}

		if (tenant) {
			const { id } = tenant;
			localStorage.setItem("tenant", JSON.stringify(tenant));

			// Store tenant_id specifically as requested
			localStorage.setItem("tenant_id", id);
		}

		// Navigate to home page
		router.push("/");
	} finally {
		// Enable the email and password fields.
	}
};

/**
 * Start implementation of the Send Email Modal
 */

const showContactModal = ref(false);
const isSending = ref(false);

const fromEmail = ref("");
const fromName = ref("");
const toEmail = ref("mike.filip@aurorasoftware.com");
const subject = ref("");
const message = ref("");

// Error states
const fromEmailError = ref("");
const fromNameError = ref("");
const subjectError = ref("");
const messageError = ref("");

// Toggle modal
const toggleContactModal = () => {
	showContactModal.value = !showContactModal.value;

	// Reset form when opening
	if (showContactModal.value) {
		resetForm();
	}
};

const resetForm = () => {
	fromEmail.value = "";
	fromName.value = "";
	subject.value = "";
	message.value = "";
	fromEmailError.value = "";
	fromNameError.value = "";
	subjectError.value = "";
	messageError.value = "";
};

// Email validation function
const isValidEmail = (email) => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

// Validate form before submission
const validateForm = () => {
	let isValid = true;

	// Validate email
	if (!fromEmail.value.trim() || !isValidEmail(fromEmail.value)) {
		fromEmailError.value = "A valid email address is required";
		isValid = false;
	} else {
		fromEmailError.value = "";
	}

	// Validate name
	if (!fromName.value.trim()) {
		fromNameError.value = "Your name is required";
		isValid = false;
	} else {
		fromNameError.value = "";
	}

	// Validate subject
	if (!subject.value.trim()) {
		subjectError.value = "Subject is required";
		isValid = false;
	} else {
		subjectError.value = "";
	}

	// Validate message
	if (!message.value.trim()) {
		messageError.value = "Message content is required";
		isValid = false;
	} else {
		messageError.value = "";
	}

	return isValid;
};

// Submit the contact form
const submitForm = async () => {
	if (!validateForm()) {
		return;
	}

	isSending.value = true;

	try {
		const response = await fetch(
			`${import.meta.env["PUBLIC_API_URL"]}/contact`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					// No Bearer token needed since user isn't logged in
				},
				body: JSON.stringify({
					from: fromEmail.value,
					to: toEmail.value,
					subject: subject.value,
					message: message.value,
					name: fromName.value.trim(),
				}),
			}
		);

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to send message");
		}

		toast.success("Your message has been sent successfully");
		toggleContactModal(); // Close modal on success
	} catch (error) {
		console.error("Error sending message:", error);
		toast.error(
			error.message ||
				"Failed to send your message. Please try again later."
		);
	} finally {
		isSending.value = false;
	}
};
</script>

<template>
	<form
		@submit.prevent="handleSubmit"
		class="w-full h-[100vh] bg-[url('/images/cover.webp')] bg-cover bg-no-repeat p-24 flex flex-col items-end justify-center"
	>
		<div
			class="border-2 border-white rounded-[20px] w-[500px] min-h-[600px] mr-32 flex flex-col gap-10 justify-center items-center bg-[#19435f] bg-opacity-80"
		>
			<div class="flex flex-col justify-center items-center mb-0">
				<img src="/images/coleman-full.png" width="60%" />
			</div>
			<div class="font-monster text-white text-[2rem] text-center -mt-6">
				Welcome to<br />Partner Portal
			</div>
			<div class="flex flex-col w-[75%] gap-y-6">
				<div class="flex flex-col gap-3">
					<label class="text-white ml-2" for="email"
						>Email Address</label
					>
					<input
						required
						type="email"
						ref="email"
						id="email"
						name="email"
						class="border-white border-2 p-2 rounded-[20px] bg-transparent px-4 text-white text-[1rem]"
					/>
				</div>
				<div class="flex flex-col gap-3">
					<label class="text-white ml-2" for="password"
						>Password</label
					>
					<input
						required
						type="password"
						ref="password"
						id="password"
						name="password"
						class="border-white border-2 p-2 rounded-[20px] bg-transparent px-4 text-white text-[1rem]"
					/>
				</div>
				<div class="flex flex-row w-full gap-2 justify-between items-center">
					<div>
						<button
							type="button"
							@click="toggleContactModal"
							class="text-white text-sm hover:underline"
						>
							Contact support
						</button>
					</div>
					<div>
						<button
							type="button"
							@click="showForgotPasswordModal = true"
							class="text-white text-sm hover:underline"
						>
							Forgot Password?
						</button>
					</div>
				</div>
			</div>
			<div class="flex flex-row w-full">
				<div class="w-full"></div>
				<div class="w-full">
					<input
						type="submit"
						class="w-[150px] !cursor-pointer font-monster flex justify-center gap-4 items-center font-semibold bg-white hover:bg-white/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-secondary p-3"
						value="Login"
					/>
				</div>
				<div class="w-full"></div>
			</div>
		</div>
		<!-- Forgot Password Modal -->
		<div
			v-if="showForgotPasswordModal"
			class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
		>
			<div
				class="bg-[#19435f] border-2 border-white rounded-[20px] p-6 w-full max-w-md mx-4"
			>
				<h3 class="text-xl font-bold mb-4 text-white">
					Reset Password
				</h3>
				<p class="text-white mb-4">
					Enter your email address to receive a password reset link.
				</p>

				<div class="mb-6">
					<label class="block text-white mb-2" for="forgot-email"
						>Email Address</label
					>
					<input
						v-model="forgotPasswordEmail"
						type="email"
						id="forgot-email"
						class="border-white border-2 p-2 rounded-[20px] bg-transparent px-4 text-white text-[1rem] w-full"
						placeholder="Enter your email address"
					/>
				</div>

				<div class="flex justify-between">
					<button
						@click="showForgotPasswordModal = false"
						type="button"
						class="px-4 py-2 border border-white text-white rounded-[10px]"
					>
						Cancel
					</button>
					<button
						@click="handleForgotPassword"
						type="button"
						:disabled="isSubmitting"
						class="px-4 py-2 bg-white text-secondary rounded-[10px] hover:bg-white/60 transition-all"
					>
						Submit
					</button>
				</div>
			</div>
		</div>
	</form>

	<!-- Contact Modal -->
	<div v-if="showContactModal" class="fixed inset-0 z-50 overflow-y-auto">
		<div
			class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20"
		>
			<!-- Background overlay -->
			<div class="fixed inset-0 transition-opacity" aria-hidden="true">
				<div
					class="absolute inset-0 bg-gray-500 opacity-75"
					@click="toggleContactModal"
				></div>
			</div>

			<!-- Modal panel -->
			<div
				class="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all w-full max-w-3xl z-50"
			>
				<div
					class="px-6 py-4 bg-secondary text-white flex justify-between items-center"
				>
					<h3 class="text-lg font-medium">Contact Support</h3>
					<button
						@click="toggleContactModal"
						class="text-white hover:text-gray-200"
					>
						<svg
							class="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				<div class="p-6">
					<p class="mb-6 text-gray-600">
						Use this form to contact our support team. We'll respond
						to your inquiry as soon as possible.
					</p>

					<form @submit.prevent="submitForm" class="space-y-4">
						<!-- From Email field -->
						<div
							class="grid grid-cols-[100px_1fr] items-center gap-2"
						>
							<label class="font-medium text-gray-700"
								>From:</label
							>
							<div class="w-full">
								<input
									type="email"
									v-model="fromEmail"
									placeholder="Your email address"
									class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none w-full"
								/>
								<p
									v-if="fromEmailError"
									class="mt-1 text-sm text-red-600"
								>
									{{ fromEmailError }}
								</p>
							</div>
						</div>

						<!-- Name field -->
						<div
							class="grid grid-cols-[100px_1fr] items-center gap-2"
						>
							<label class="font-medium text-gray-700"
								>Name:</label
							>
							<div class="w-full">
								<input
									type="text"
									v-model="fromName"
									placeholder="Your name"
									class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none w-full"
								/>
								<p
									v-if="fromNameError"
									class="mt-1 text-sm text-red-600"
								>
									{{ fromNameError }}
								</p>
							</div>
						</div>

						<!-- To field (read-only) -->
						<div
							class="grid grid-cols-[100px_1fr] items-center gap-2"
						>
							<label class="font-medium text-gray-700">To:</label>
							<input
								type="text"
								v-model="toEmail"
								readonly
								class="rounded-[10px] px-4 py-3 bg-gray-100 border-[1px] border-stroke font-monster border-solid outline-none w-full cursor-not-allowed"
							/>
						</div>

						<!-- Subject field -->
						<div
							class="grid grid-cols-[100px_1fr] items-center gap-2"
						>
							<label class="font-medium text-gray-700"
								>Subject:</label
							>
							<div class="w-full">
								<input
									type="text"
									v-model="subject"
									placeholder="Enter message subject"
									class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none w-full"
								/>
								<p
									v-if="subjectError"
									class="mt-1 text-sm text-red-600"
								>
									{{ subjectError }}
								</p>
							</div>
						</div>

						<!-- Message body with WYSIWYG editor -->
						<div>
							<label class="font-medium text-gray-700 block mb-2"
								>Message:</label
							>
							<div class="w-full">
								<Editor
									api-key="{{import.meta.env.PUBLIC_TINYMCE_API_KEY}}"
									v-model="message"
									:init="{
										height: 250,
										menubar: false,
										plugins: [
											'advlist',
											'autolink',
											'lists',
											'link',
											'charmap',
											'preview',
											'anchor',
											'searchreplace',
											'visualblocks',
											'insertdatetime',
											'table',
											'code',
											'help',
											'wordcount',
										],
										toolbar:
											'undo redo | blocks | ' +
											'bold italic forecolor | alignleft aligncenter ' +
											'alignright alignjustify | bullist numlist outdent indent | ' +
											'removeformat | help',
										content_style:
											'body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }',
									}"
								/>
								<p
									v-if="messageError"
									class="mt-1 text-sm text-red-600"
								>
									{{ messageError }}
								</p>
							</div>
						</div>

						<!-- Action buttons -->
						<div class="flex justify-end space-x-3 pt-4">
							<button
								type="button"
								@click="toggleContactModal"
								class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
							>
								Cancel
							</button>
							<button
								type="submit"
								:disabled="isSending"
								class="bg-secondary hover:bg-secondary/90 text-white font-medium py-2 px-6 rounded-lg flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
							>
								<span
									v-if="isSending"
									class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
								></span>
								Send Message
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>
